import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { authApi } from '@/api/endpoints/auth';
import { isApiError } from '@/api/errors';
import type { User } from '@/api/types';
import {
  clearAttribution,
  clearGuestSessionFlags,
  getRegistrationAttribution,
} from '@/analytics/attribution';
import { track } from '@/analytics/tracker';
import { storageKeys, uiFlagKeys } from '@/config/app';
import { local } from '@/lib/storage';
import { mapUser } from './mapUser';

export interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /**
   * Ответил ли сервер про доступ в ТЕКУЩЕЙ сессии страницы. Не выводится из
   * полей пользователя (они гидратируются из localStorage прошлого визита) и
   * не персистится — поднимается только успешным /me или /auth/google.
   */
  isAccessResolved: boolean;
  /** Email, ожидающий код подтверждения (после регистрации или входа с неподтверждённым email, AUTH-4). */
  pendingVerificationEmail: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  verifyEmail: (code: string) => Promise<void>;
  resendVerificationCode: () => Promise<void>;
  /** Перевод на шаг кода для уже существующего, но неподтверждённого email (403 на /login). */
  beginVerification: (email: string) => Promise<void>;
  cancelVerification: () => void;
  googleSignIn: (idToken: string) => Promise<void>;
  logout: (source?: 'manual' | 'auto_401') => void;
  fetchUser: () => Promise<void>;
  /** Локальное обновление пользователя после серверных мутаций (/me/onboarding-complete и т.п.). */
  patchUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Код причины для аналитики: ни email, ни текста пользователя в телеметрию не уходит. */
export function toAuthErrorType(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 401) return 'invalid_credentials';
    if (error.status === 403) return 'email_not_verified';
    if (error.status === 422) return 'validation';
    if (error.status === 429) return 'rate_limited';
    if (error.status > 0) return `http_${error.status}`;
  }
  return 'network';
}

function readStoredUser(): User | null {
  return local.getJson<User>(storageKeys.user);
}

function persistSession(token: string | null, user: User | null): void {
  if (token) local.set(storageKeys.token, token);
  else local.remove(storageKeys.token);
  if (user) local.setJson(storageKeys.user, user);
  else local.remove(storageKeys.user);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Токен и пользователь читаются синхронно на первом рендере: иначе вернувшийся
  // пользователь на миг выглядел бы гостем и уходил бы ложный guest-visit.
  const [token, setToken] = useState<string | null>(() => local.get(storageKeys.token));
  const [user, setUser] = useState<User | null>(() =>
    local.get(storageKeys.token) ? readStoredUser() : null,
  );
  const [isAccessResolved, setIsAccessResolved] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const userRef = useRef(user);
  userRef.current = user;

  const clearLocal = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAccessResolved(false);
    setPendingVerificationEmail(null);
    persistSession(null, null);
    // AUTH-10: локальные флаги интерфейса чистятся; тема и язык — предпочтения, остаются.
    for (const key of uiFlagKeys) local.remove(key);
    clearGuestSessionFlags();
  }, []);

  const logout = useCallback(
    (source: 'manual' | 'auto_401' = 'manual') => {
      track('logout', { metadata: { source, user_id: userRef.current?.id ?? null } });
      if (source === 'manual') authApi.logout().catch(() => {});
      clearLocal();
    },
    [clearLocal],
  );

  // 401 от API: клиент уже убрал токен и разослал событие с user_id.
  useEffect(() => {
    const onForceLogout = (event: Event) => {
      const detail = (event as CustomEvent<{ user_id?: number | null }>).detail;
      track('logout', { metadata: { source: 'auto_401', user_id: detail?.user_id ?? null } });
      clearLocal();
    };
    window.addEventListener('auth:logout', onForceLogout);
    return () => window.removeEventListener('auth:logout', onForceLogout);
  }, [clearLocal]);

  const fetchUser = useCallback(async () => {
    if (!local.get(storageKeys.token)) return;
    try {
      const data = await authApi.me();
      const next = mapUser(data.user);
      setUser(next);
      local.setJson(storageKeys.user, next);
      setIsAccessResolved(true);
    } catch (error) {
      // 401 обработан клиентом (auth:logout); прочие отказы оставляют гидратированного пользователя.
      if (import.meta.env.DEV) console.warn('[auth] /me failed', error);
    }
  }, []);

  useEffect(() => {
    if (token) void fetchUser();
  }, [token, fetchUser]);

  const adoptSession = useCallback((accessToken: string, nextUser: User, resolved: boolean) => {
    setToken(accessToken);
    setUser(nextUser);
    setPendingVerificationEmail(null);
    if (resolved) setIsAccessResolved(true);
    persistSession(accessToken, nextUser);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      track('login_attempt', { metadata: { method: 'password' } });
      try {
        const data = await authApi.login(email, password);
        const nextUser = mapUser(data.user);
        adoptSession(data.access_token, nextUser, false);
        track('login_success', { metadata: { method: 'password', user_id: nextUser.id } });
      } catch (error) {
        track('login_failed', {
          metadata: { method: 'password', error_type: toAuthErrorType(error) },
        });
        throw error;
      }
    },
    [adoptSession],
  );

  const register = useCallback(async (name: string, email: string, password: string) => {
    track('register_attempt', { metadata: { method: 'password' } });
    try {
      const data = await authApi.register({
        name,
        email,
        password,
        ...getRegistrationAttribution(),
      });
      const nextUser = mapUser(data.user);
      // Токена нет до подтверждения email (AUTH-4).
      setPendingVerificationEmail(nextUser.email);
      clearAttribution();
      track('register_success', { metadata: { method: 'password', user_id: nextUser.id } });
    } catch (error) {
      track('register_failed', {
        metadata: { method: 'password', error_type: toAuthErrorType(error) },
      });
      throw error;
    }
  }, []);

  const verifyEmail = useCallback(
    async (code: string) => {
      const email = pendingVerificationEmail ?? userRef.current?.email;
      if (!email) throw new Error('No email to verify');
      try {
        const data = await authApi.verifyCode(email, code);
        const nextUser = mapUser(data.user);
        adoptSession(data.access_token, nextUser, false);
        track('email_verification_success', { metadata: { user_id: nextUser.id } });
      } catch (error) {
        track('email_verification_failed', { metadata: { error_type: toAuthErrorType(error) } });
        throw error;
      }
    },
    [adoptSession, pendingVerificationEmail],
  );

  const resendVerificationCode = useCallback(async () => {
    const email = pendingVerificationEmail ?? userRef.current?.email;
    if (!email) throw new Error('No email to verify');
    try {
      await authApi.sendVerificationCode(email);
      track('email_verification_resend');
    } catch (error) {
      track('email_verification_resend', { metadata: { error_type: toAuthErrorType(error) } });
      throw error;
    }
  }, [pendingVerificationEmail]);

  const beginVerification = useCallback(async (email: string) => {
    setPendingVerificationEmail(email);
    // Код уходит сразу; отказ не мешает пользователю нажать «Send again».
    await authApi.sendVerificationCode(email).catch(() => {});
  }, []);

  const cancelVerification = useCallback(() => setPendingVerificationEmail(null), []);

  const googleSignIn = useCallback(
    async (idToken: string) => {
      let data;
      try {
        data = await authApi.googleSignIn(idToken, getRegistrationAttribution());
      } catch (error) {
        track('google_oauth_failed', {
          metadata: { source: 'token_exchange', error_type: toAuthErrorType(error) },
        });
        throw error;
      }
      const nextUser = mapUser(data.user);
      // /auth/google отдаёт те же поля доступа, что и /me.
      adoptSession(data.access_token, nextUser, true);
      clearAttribution();
      track('login_success', { metadata: { method: 'google', user_id: nextUser.id } });
    },
    [adoptSession],
  );

  const patchUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      local.setJson(storageKeys.user, next);
      return next;
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: !!token,
      isAccessResolved,
      pendingVerificationEmail,
      login,
      register,
      verifyEmail,
      resendVerificationCode,
      beginVerification,
      cancelVerification,
      googleSignIn,
      logout,
      fetchUser,
      patchUser,
    }),
    [
      user,
      token,
      isAccessResolved,
      pendingVerificationEmail,
      login,
      register,
      verifyEmail,
      resendVerificationCode,
      beginVerification,
      cancelVerification,
      googleSignIn,
      logout,
      fetchUser,
      patchUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
